/* Inject — the second door (BUILD-JOURNEY stage 1): paste a ready JSON
 * document directly. Works for any definition type; the validator still
 * gates the save. CREATE goes through POST /defs/{type} — the engine MINTS
 * the id (wf_/jrn_/src_<12hex>), so there is no id field here; any id in
 * the pasted JSON is stripped before send (it would be ignored anyway). */
import React, {
    useState
} from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from 'shared/components/ui/dialog';
import {
    FilePlus2,
    Loader2
} from 'lucide-react';
import {
    appEngineService,
    describeError,
    extractValidatorErrors
} from '../api/appEngine.service';
import {
    useAppStudio
} from '../AppStudioContext';
import CodeMirrorJson from './CodeMirrorJson';
import {
    notify
} from 'shared/api/notifier';
import type {
    DefType
} from '../api/types';

type Props = {
    type: DefType;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Receives the ENGINE-MINTED id of the created document. */
    onCreated: (id: string) => void;
};

const STARTER = '{\n    "name": ""\n}';

/** Drop author-supplied ids — minting is the engine's job. Top-level `id`
 * always; source docs also carry one under `source.id`. */
export function stripIds(doc: any, type: DefType): any {
    if (!doc || typeof doc !== 'object') return doc;
    const out = {
        ...doc
    };
    delete out.id;
    if (type === 'source' && out.source && typeof out.source === 'object') {
        out.source = {
            ...out.source
        };
        delete out.source.id;
    }
    return out;
}

export default function InjectDialog({
    type,
    open,
    onOpenChange,
    onCreated
}: Props) {
    const {
        tenancy
    } = useAppStudio();
    const [text, setText] = useState(STARTER);
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    const save = async () => {
        let doc: any;
        try {
            doc = JSON.parse(text);
        } catch (e: any) {
            setErrors([`Not valid JSON: ${e?.message || ''}`]);
            return;
        }
        setBusy(true);
        try {
            const saved = await appEngineService.createDef(
                tenancy, type, stripIds(doc, type));
            const mintedId = String(saved?.id || saved?.source?.id || '');
            if (!mintedId) {
                setErrors(['Create succeeded but the response carried no id — refresh the list.']);
                return;
            }
            notify(`Created ${type} ${mintedId}`, 'success');
            setText(STARTER);
            setErrors([]);
            onCreated(mintedId);
        } catch (err) {
            const ve = extractValidatorErrors(err);
            if (ve) setErrors(ve);
            else notify(describeError(err), 'danger');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[680px]">
                <DialogHeader>
                    <DialogTitle style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                    }}>
                        <FilePlus2 size={18} color="var(--aps-primary)" />
                        New {type} — paste JSON
                    </DialogTitle>
                    <DialogDescription>
                        Ask your LLM for the document (omit &quot;id&quot; — the system
                        mints it on create), paste it here. The validator gates the
                        save — invalid JSON physically cannot land.
                    </DialogDescription>
                </DialogHeader>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10
                }}>
                    <div className="aps-editor-frame" style={{
                        height: '44vh',
                        flex: 'none'
                    }}>
                        <CodeMirrorJson value={text} onChange={setText} />
                    </div>
                    <div style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center'
                    }}>
                        <span className="aps-help" style={{
                            marginTop: 0,
                            flex: 1
                        }}>
                            No id needed — the engine mints one and the editor opens on it.
                        </span>
                        <button className="aps-btn aps-btn-primary" disabled={busy} onClick={save}>
                            {busy ? <Loader2 size={15} className="aps-spin" /> : <FilePlus2 size={15} />}
                            Validate &amp; Create
                        </button>
                    </div>
                    {errors.length > 0 && (
                        <div className="aps-errors">
                            <p className="aps-errors-title">Rejected</p>
                            <ul>
                                {errors.map((e, i) => <li key={i}>{String(e)}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
